import React, { useRef, useCallback, useState, useMemo, useEffect } from 'react';
import { Card, Button, Typography, Input, Space, Tooltip, Modal, notification, InputNumber } from 'antd';
import { CloseOutlined, DeleteOutlined, ShoppingCartOutlined, SwapOutlined, ShareAltOutlined, CopyOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import QRCode from 'react-qr-code';
import { encodeCart, buildShareLink } from '../../utils/cartShare';
import { useGroceryCart } from '../../context/GroceryCartContext';
import { getCurrentListId } from '../../utils/groceryListSession';
import { formatPriceWithCurrency, getFacilityCurrency, preloadCustomCurrencies } from '../../utils/currency';

const { Text, Title } = Typography;

const MIN_SIDE = 320;
const MAX_SIDE = 800;
const MIN_BOTTOM = 200;
const MAX_BOTTOM = 600;
const HEADER_HEIGHT = 80; // Header height to avoid overlap

const ResizableSlideover = () => {
  const {
    items,
    orientation,
    size,
    isOpen,
    totalItems,
    updateQuantity,
    removeItem,
    clear,
    setOpen,
    setOrientation,
    setSize,
    addItem
  } = useGroceryCart();
  const [shareOpen, setShareOpen] = useState(false);
  const [listId, setListId] = useState(null);
  const [facilityCurrency, setFacilityCurrency] = useState('USD');
  const [api, contextHolder] = notification.useNotification();
  const [position, setPosition] = useState({ top: HEADER_HEIGHT, right: 0 }); // Position for side mode
  const positionRef = useRef({ x: 0, y: 0, isDragging: false });
  const [quantityModal, setQuantityModal] = useState({ open: false, item: null, existingItem: null, quantityToAdd: 1 });

  const facilityId = useMemo(() => {
    // Attempt to derive facilityId from current pathname (/:facilityId/...)
    const seg = window.location.pathname.split('/').filter(Boolean)[0];
    return seg || 'unknown';
  }, []);

  // Get current list ID
  useEffect(() => {
    const currentListId = getCurrentListId(facilityId);
    setListId(currentListId);
  }, [facilityId]);

  // Load facility currency and preload custom currencies
  useEffect(() => {
    if (!facilityId || facilityId === 'unknown') return;
    
    const loadCurrency = async () => {
      try {
        const currency = await getFacilityCurrency(facilityId);
        setFacilityCurrency(currency);
        // Preload custom currencies for proper formatting
        await preloadCustomCurrencies(facilityId);
      } catch (error) {
        console.error('Error loading currency:', error);
      }
    };
    
    loadCurrency();
  }, [facilityId]);

  const shareCode = useMemo(() => encodeCart(items, facilityId), [items, facilityId]);
  const shareLink = useMemo(() => {
    const baseLink = buildShareLink(shareCode);
    // Add listId to the URL
    return listId ? `${baseLink}&listId=${listId}` : baseLink;
  }, [shareCode, listId]);

  // Handle resizing (width/height)
  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startPos = orientation === 'side' ? e.clientX : e.clientY;
    const startSize = size;

    const onMove = (ev) => {
      if (orientation === 'side') {
        const delta = startPos - ev.clientX;
        let newSize = startSize + delta;
        newSize = Math.max(MIN_SIDE, Math.min(MAX_SIDE, newSize));
        setSize(newSize);
      } else {
        const delta = ev.clientY - startPos;
        let newSize = startSize + delta;
        newSize = Math.max(MIN_BOTTOM, Math.min(MAX_BOTTOM, newSize));
        setSize(newSize);
      }
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Handle position dragging (for side mode)
  const startPositionDrag = (e) => {
    if (orientation !== 'side') return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = { ...position };
    positionRef.current.isDragging = true;

    const onMove = (ev) => {
      const deltaX = startX - ev.clientX;
      const deltaY = ev.clientY - startY;
      
      let newRight = Math.max(0, startPos.right + deltaX);
      let newTop = Math.max(HEADER_HEIGHT, Math.min(window.innerHeight - size - 20, startPos.top + deltaY));
      
      setPosition({ top: newTop, right: newRight });
    };

    const onUp = () => {
      positionRef.current.isDragging = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const toggleOrientation = useCallback(() => {
    setOrientation(orientation === 'side' ? 'bottom' : 'side');
  }, [orientation, setOrientation]);

  if (!isOpen) {
    return (
      <Button 
        type="primary" 
        icon={<ShoppingCartOutlined />} 
        style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
        onClick={() => setOpen(true)}
      >
        List ({totalItems})
      </Button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: orientation === 'side' ? position.top : 'auto',
        bottom: orientation === 'bottom' ? 0 : 'auto',
        right: orientation === 'side' ? position.right : 0,
        height: orientation === 'side' ? `calc(100vh - ${position.top}px)` : size,
        width: orientation === 'side' ? size : '100%',
        background: 'var(--surface-primary)',
        borderLeft: orientation === 'side' ? '1px solid var(--border-primary)' : 'none',
        borderTop: orientation === 'bottom' ? '1px solid var(--border-primary)' : 'none',
        boxShadow: '0 0 12px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        transition: positionRef.current.isDragging ? 'none' : 'width .15s, height .15s, top .15s, right .15s',
        borderRadius: orientation === 'side' ? '8px 0 0 8px' : '0'
      }}
    >
      {/* Resize Handle */}
      <div
        onMouseDown={startResize}
        style={{
          cursor: orientation === 'side' ? 'col-resize' : 'row-resize',
          width: orientation === 'side' ? 6 : '100%',
          height: orientation === 'side' ? '100%' : 6,
          position: 'absolute',
          left: orientation === 'side' ? 0 : 0,
          top: orientation === 'bottom' ? 0 : 0,
          background: 'transparent',
          zIndex: 10
        }}
      />

      {/* Header - Draggable for side mode */}
      <div 
        onMouseDown={startPositionDrag}
        style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid var(--border-secondary)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          color: 'var(--lcolor)',
          cursor: orientation === 'side' ? 'move' : 'default',
          userSelect: 'none'
        }}
      >
        <Space>
          <ShoppingCartOutlined />
          <Text strong style={{ color: 'var(--lcolor)' }}>Grocery List</Text>
          <Text type="secondary" style={{ color: 'var(--lcolor)' }}>{totalItems} items</Text>
        </Space>
        <Space>
          <Tooltip title="Share / Export">
            <Button 
              size="small" 
              style={{ color: 'var(--lcolor)' }} 
              type="text" 
              icon={<ShareAltOutlined />} 
              onClick={(e) => { e.stopPropagation(); setShareOpen(true); }} 
              disabled={!items.length}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Tooltip>
          <Tooltip title={orientation === 'side' ? 'Move to bottom' : 'Move to side'}>
            <Button 
              size="small" 
              style={{ color: 'var(--lcolor)' }} 
              type="text" 
              icon={<SwapOutlined />} 
              onClick={(e) => { e.stopPropagation(); toggleOrientation(); }}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Tooltip>
          <Tooltip title="Clear list">
            <Button 
              size="small" 
              danger 
              type="text" 
              icon={<DeleteOutlined />} 
              onClick={(e) => { e.stopPropagation(); clear(); }} 
              disabled={!items.length}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Tooltip>
          <Tooltip title="Close">
            <Button 
              size="small" 
              style={{ color: 'var(--lcolor)' }} 
              type="text" 
              icon={<CloseOutlined />} 
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Items - Drop Zone */}
      <div 
        data-drop-zone="true"
        style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '8px 12px', 
          color: 'var(--lcolor)',
          minHeight: '200px'
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'copy';
          e.currentTarget.style.backgroundColor = 'var(--surface-tertiary)';
          e.currentTarget.style.border = '2px dashed var(--primary-color)';
        }}
        onDragLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.border = 'none';
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.border = 'none';
          const itemData = e.dataTransfer.getData('application/json');
          if (itemData) {
            try {
              const item = JSON.parse(itemData);
              const existingItem = items.find(i => i.name === item.name);
              if (existingItem) {
                // Item already exists - show quantity modal
                setQuantityModal({ open: true, item, existingItem, quantityToAdd: 1 });
              } else {
                addItem({ ...item, quantity: 1 });
                api.success({ message: 'Item Added', description: `${item.name} added to your list` });
              }
            } catch (err) {
              console.error('Error parsing dropped item:', err);
            }
          }
        }}
        onTouchOver={(e) => {
          // Mobile drop zone highlight
          e.currentTarget.style.backgroundColor = 'var(--surface-tertiary)';
        }}
      >
        {!items.length && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--lcolor)' }}>
            <Text type="secondary">Drag items here or search for products to add them to your list.</Text>
          </div>
        )}
        {items.map(item => (
          <Card key={item.key} size="small" style={{ marginBottom: 8 }} bodyStyle={{ padding: 8 }}>
            <div style={{
              color: 'var(--lcolor)',
              display: 'flex', 
              flexDirection: window.innerWidth < 600 ? 'column' : 'row',
              gap: window.innerWidth < 600 ? 8 : 0,
              justifyContent: 'space-between', 
              alignItems: window.innerWidth < 600 ? 'flex-start' : 'center' 
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text strong style={{ 
                  display: 'block', 
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  color: 'var(--lcolor)'
                }}>
                  {item.name}
                </Text>
                <Text type="secondary" style={{ 
                  fontSize: 11,
                  wordBreak: 'break-all',
                  whiteSpace: 'normal'
                }}>
                  {item.baseId}
                </Text>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8,
                flexWrap: 'wrap',
                width: window.innerWidth < 600 ? '100%' : 'auto'
              }}>
                <Space.Compact size="small">
                  <Button 
                    icon={<MinusOutlined />}
                    onClick={() => updateQuantity(item.key, Math.max(1, item.quantity - 1))}
                    style={{ fontSize: '16px', padding: '0 8px' }}
                  />
                  <Input
                    type="number"
                    min={1}
                    value={item.quantity}
                    onChange={(e) => updateQuantity(item.key, parseInt(e.target.value) || 1)}
                    style={{ width: 60, textAlign: 'center' }}
                    size="small"
                  />
                  <Button 
                    icon={<PlusOutlined />}
                    onClick={() => updateQuantity(item.key, item.quantity + 1)}
                    style={{ fontSize: '16px', padding: '0 8px' }}
                  />
                </Space.Compact>
                <Text strong style={{ 
                  width: 80, 
                  textAlign: 'right', 
                  color: 'var(--success-color)',
                  whiteSpace: 'nowrap',
                  fontSize: '14px'
                }}>
                  {formatPriceWithCurrency(item.price * item.quantity, item.currency || facilityCurrency, facilityId)}
                </Text>
                <Button 
                  type="text" 
                  danger 
                  size="small" 
                  icon={<CloseOutlined style={{ fontSize: '18px' }} />}
                  onClick={() => removeItem(item.key)}
                  style={{ padding: '4px 8px' }}
                />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: 12, borderTop: '1px solid var(--border-secondary)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>Total:</Text>
          <div style={{ textAlign: 'right' }}>
            {(() => {
              // Group items by currency
              const itemsByCurrency = items.reduce((acc, item) => {
                const currency = item.currency || facilityCurrency;
                if (!acc[currency]) {
                  acc[currency] = { total: 0, items: [] };
                }
                acc[currency].total += item.price * item.quantity;
                acc[currency].items.push(item);
                return acc;
              }, {});

              const currencies = Object.keys(itemsByCurrency);
              
              if (currencies.length > 1) {
                // Show separate totals for each currency
                return (
                  <div>
                    {currencies.map(currency => (
                      <div key={currency} style={{ marginBottom: '4px' }}>
                        <Text strong style={{ color: 'var(--primary-color)', fontSize: '14px' }}>
                          {formatPriceWithCurrency(itemsByCurrency[currency].total, currency, facilityId)}
                        </Text>
                      </div>
                    ))}
                  </div>
                );
              } else {
                // Show single total - use the actual currency from items, not facility currency
                const singleCurrency = currencies[0] || facilityCurrency;
                const singleTotal = itemsByCurrency[singleCurrency]?.total || 0;
                return (
                  <Title level={4} style={{ margin: 0, color: 'var(--primary-color)' }}>
                    {formatPriceWithCurrency(singleTotal, singleCurrency, facilityId)}
                  </Title>
                );
              }
            })()}
          </div>
        </div>
      </div>
      <Modal
        open={shareOpen}
        onCancel={() => setShareOpen(false)}
        footer={null}
        title="Share Grocery List"
        width={400}
      >
        {contextHolder}
        {!items.length ? <Text type="secondary">No items to share.</Text> : (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ background: '#fff', padding: 12, borderRadius: 8 }}>
                <QRCode value={shareCode} size={180} />
              </div>
            </div>
            <div>
              <Text strong>Manual Code:</Text>
              <Input value={shareCode} readOnly style={{ marginTop: 4 }} />
            </div>
            <div>
              <Text strong>Share Link:</Text>
              <Input value={shareLink} readOnly style={{ marginTop: 4 }} />
            </div>
            <Space>
              <Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(shareCode); api.success({ message: 'Code Copied' }); }}>Copy Code</Button>
              <Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(shareLink); api.success({ message: 'Link Copied' }); }}>Copy Link</Button>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>Cashier can scan QR or paste code/link to import this list.</Text>
          </Space>
        )}
      </Modal>

      {/* Quantity Modal for Duplicate Items */}
      <Modal
        open={quantityModal.open}
        title="Item Already in List"
        onOk={() => {
          if (quantityModal.existingItem && quantityModal.quantityToAdd > 0) {
            updateQuantity(quantityModal.existingItem.key, quantityModal.existingItem.quantity + quantityModal.quantityToAdd);
            api.success({ message: `Added ${quantityModal.quantityToAdd} more ${quantityModal.item.name}` });
            setQuantityModal({ open: false, item: null, existingItem: null, quantityToAdd: 1 });
          }
        }}
        onCancel={() => setQuantityModal({ open: false, item: null, existingItem: null, quantityToAdd: 1 })}
        okText="Add"
        cancelText="Cancel"
      >
        {quantityModal.item && (
          <div style={{ marginTop: '16px' }}>
            <Text >{quantityModal.item.name} is already in your list (current quantity: {quantityModal.existingItem?.quantity || 0}).</Text>
            <div style={{ marginTop: '12px' }}>
              <Text >How many more would you like to add?</Text>
              <InputNumber
                min={1}
                value={quantityModal.quantityToAdd || 1}
                style={{ width: '100%', marginTop: '8px' }}
                onChange={(val) => setQuantityModal(prev => ({ ...prev, quantityToAdd: val || 1 }))}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ResizableSlideover;
